import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ISlide, Slide } from 'src/app/core/dtos/interfaces/i-slide';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { MediaService } from 'src/app/shared/services/media.service';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { MediaAdderDialogComponent } from '../media-adder-dialog/media-adder-dialog.component';
import { MediaTypes } from '../../enums/media-types';
import { ImgOrVideo } from '../../types/media-type';
import { SlideEvent } from 'src/app/core/dtos/interfaces/i-slide-event';
import { Subject } from 'rxjs';
import { ILinkInsert } from 'src/app/core/dtos/interfaces/i-link-insert';
import { SlideComponent } from '../slide/slide.component';

@Component({
  selector: 'app-slide-list',
  templateUrl: './slide-list.component.html',
  styleUrls: ['./slide-list.component.scss'],
})
export class SlideListComponent {
  @Input() slideArray: FormArray<FormControl<ISlide>>;
  @Input() isDisabled = false;

  changingSlideOrder$ = new Subject<boolean>();
  onAddToBody$ = new Subject<ILinkInsert>();
  onRemoveLink$ = new Subject<string>();

  @ViewChildren(SlideComponent) slideComponents!: QueryList<SlideComponent>;

  ADD_MEDIA_TYPE_HANDLERS = {
    [MediaTypes.IMAGE]: (newImgs: ImgOrVideo[]) => {
      newImgs.forEach((ni) => {
        this.createNewSlide(ni);
      });
    },
  };

  MEDIA_TYPES = MediaTypes;
  currentBookmark: ISlide;

  constructor(
    public mediaService: MediaService,
    private dialogService: DialogService,
    public formBuilder: FormBuilder
  ) {}

  createNewSlide(newImage?: IImgMediaDto): void {
    var index = this.slideArray.length ? this.slideArray.length + 1 : 1;
    var newSlide = new Slide(this.calculateNewSlideId(index), index);

    if (newImage) {
      newSlide.mediaId = newImage.id;
      newSlide.mediaThumbnailUrl = newImage.thumbnailUrl;
      newSlide.mediaTitle = newImage.name;
      newSlide.mediaType = this.MEDIA_TYPES.IMAGE;
      newSlide.caption = newImage.caption ? newImage.caption : '';
    }

    this.slideArray.push(new FormControl(newSlide));

    this.restoreSlideOrder();
  }

  calculateNewSlideId(index: number): number {
    let idList = this.slideArray.controls.map((slide) => slide.value.id);
    while (idList.includes(index)) {
      index++;
    }
    return index;
  }

  createGallerySlides(): void {
    var mediaType = this.MEDIA_TYPES.IMAGE;
    this.dialogService
      .openCustomComponentDialog(
        MediaAdderDialogComponent,
        {
          mediaType,
          prevSelectedMedias: [],
          canSelectMultiple: true,
        },
        true,
        { width: '95%', height: '95vh', maxWidth: '95vw' }
      )
      .afterClosed()
      .subscribe({
        next: ({
          actionResult,
          data,
        }: {
          actionResult: boolean;
          data: ImgOrVideo[];
        }) => {
          if (actionResult) {
            this.ADD_MEDIA_TYPE_HANDLERS[mediaType](data);
          }
        },
      });
  }

  drop(event: CdkDragDrop<any>) {
    if (this.isDisabled) {
      return;
    }
    this.handleSlideOrder(event.previousIndex, event.currentIndex);
  }

  slideOrderChanged(slideIndexes: number[]): void {
    this.changingSlideOrder$.next(true);
    var newSlideIndex = slideIndexes[0] - 1;
    var previousIndex = slideIndexes[1] - 1;

    newSlideIndex =
      newSlideIndex > this.slideArray.length
        ? this.slideArray.length
        : newSlideIndex;

    newSlideIndex = newSlideIndex < 0 ? 0 : newSlideIndex;

    if (newSlideIndex === previousIndex) {
      return;
    }

    this.handleSlideOrder(previousIndex, newSlideIndex);
  }

  handleSlideOrder(previousIndex, nextIndex): void {
    moveItemInArray(this.slideArray.controls, previousIndex, nextIndex);
    this.restoreSlideOrder();
  }

  removeSlideClick(slideIndex: number): void {
    this.slideArray.removeAt(slideIndex);
    this.restoreSlideOrder();
  }

  restoreSlideOrder(): void {
    this.slideArray.controls.forEach((slide, index) => {
      var newSlide = slide;
      newSlide.value.order = index + 1;
      slide.setValue(newSlide.value);
    });
    setTimeout(() => {
      this.changingSlideOrder$.next(false);
    }, 0);
  }

  slideChanged(event: SlideEvent): void {
    this.slideArray.controls.forEach((slide) => {
      if (slide.value.id === event.value.id) {
        slide.setValue(event.value);
      }
    });
    this.restoreSlideOrder();
  }

  bookmarkChanged(event: ISlide): void {
    this.currentBookmark = event;
  }

  onAddToBody($event) {
    const bookmark = this.currentBookmark;
    if (!bookmark || !this.slideComponents) return;

    const bookmarkedSlide = this.slideComponents.find(
      (slide) => slide.form.value.id === bookmark.id
    );

    if (bookmarkedSlide) {
      bookmarkedSlide.onAddToBody($event);
    }
  }

  onRemoveLink($event) {
    if (!this.slideComponents) return;

    this.slideComponents.forEach((slide) => {
      slide.onRemoveLink($event);
    });
  }

  dragStarted() {
    this.changingSlideOrder$.next(true);
  }
}
